/* ek-bevegelse: bevegelsen fra www (12.10).
   1) Avsløring ved rulling, som src/js/inView.js: blokker, kort, overskrifter og bilder ligger 15 px nede og
      usynlige, og glir opp (0,8 s expo.out) og tones inn (0,4 s sine.out) med 0,15 s forsinkelse når toppen
      kommer inn i bildet. Kort i samme rad forskyves 100 ms hver. På www står data-reveal i malene; her
      finner skriptet blokkene selv inne i <main>, så malene ikke må endres.
   2) Bilder som lastes sent tones inn på 0,5 s (lazysizes-klassene i www sin _images.css).
   Trygghet: ingenting skjules før skriptet har satt html.ek-bev; ved feil fjernes klassen og alt vises.
   Toppen, menyene, kurvskuffen, søkelaget, Kjøp sammen-vinduet og app-blokker røres ikke.
   Respekterer prefers-reduced-motion og står av i temaredigereren. */
(() => {
  'use strict';
  const html = document.documentElement;
  const main = document.getElementById('main') || document.querySelector('main');
  if (!main || !window.requestAnimationFrame) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  // Det som aldri skal få bevegelse, verken selv eller som del av en blokk.
  const UNNTAK = [
    '[data-ek-topp]', '.ek-skuff', '.ek-sokelag', '[class*="bundles-bundler"]', '.shopify-app-block',
    'iframe', 'dialog', 'template', 'script', 'style', 'noscript', 'link', 'input[type="hidden"]',
    '.ek-hero > .absolute', '.ek-fullbilde > img', '[data-ek-uten-bevegelse]',
  ].join(',');
  // Blokker som glir inn som én enhet (data-reveal på www: kort, tekstblokker, bilder, overskrifter).
  const ENHET = [
    '.ek-kort', '.ek-sider-mur__kort', '.ek-trekkspill', '.ek-karusell', '.ek-sider-prosa', '.ek-prosa',
    '.ek-ingress', '.prose', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'blockquote', 'ul', 'ol',
    'dl', 'table', 'form', 'img', 'video', 'picture', 'hr', 'details',
  ].join(',');
  // Barn som ikke gjør en boks til en «beholder»: en rad med lenker eller knapper glir inn samlet.
  const INLINE = new Set(['A', 'SPAN', 'BUTTON', 'SVG', 'svg', 'STRONG', 'EM', 'B', 'I', 'SUP', 'SUB', 'BR',
    'SMALL', 'ABBR', 'IMG', 'PICTURE', 'INPUT', 'LABEL', 'SELECT', 'TIME', 'S', 'DEL', 'INS', 'MARK']);

  const venter = new Set();
  let lytter = false;
  let planlagt = false;

  const hoppOver = (el) => el.matches(UNNTAK) || el.hidden;
  const harUnntakInni = (el) => !!el.querySelector(UNNTAK);
  const festet = (el) => { const p = getComputedStyle(el).position; return p === 'fixed' || p === 'sticky'; };
  const erKort = (el) => /\bcol-span-/.test(el.className && el.className.toString()) &&
    el.querySelectorAll('.ek-kort__bilde').length === 1;
  const erBlad = (el) => {
    for (const c of el.children) if (!INLINE.has(c.tagName)) return false;
    return true;
  };

  function finnEnheter(el, ut) {
    if (hoppOver(el)) return;
    const kandidat = el.matches(ENHET) || erKort(el) || erBlad(el);
    if (kandidat && !harUnntakInni(el) && !festet(el)) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) ut.push(el);
      return;
    }
    for (const c of el.children) finnEnheter(c, ut);
  }

  function ferdig(el) {
    el.removeAttribute('data-ek-vis');
    el.classList.remove('ek-vist');
    el.style.removeProperty('--ek-forsinkelse');
    if (el.getAttribute('style') === '') el.removeAttribute('style');
  }

  function vis(el, nr) {
    const ms = 150 + 100 * nr;
    el.style.setProperty('--ek-forsinkelse', ms + 'ms');
    el.classList.add('ek-vist');
    setTimeout(() => ferdig(el), ms + 850);
  }

  function sjekk() {
    planlagt = false;
    const h = window.innerHeight;
    const nye = [];
    venter.forEach((el) => {
      if (!el.isConnected) { venter.delete(el); return; }
      const r = el.getBoundingClientRect();
      if (r.top - 15 < h) nye.push([el, r]);
    });
    nye.sort((a, b) => (a[1].top - b[1].top) || (a[1].left - b[1].left));
    let radTopp = null;
    let nr = 0;
    for (const [el, r] of nye) {
      venter.delete(el);
      // Allerede rullet forbi (f.eks. etter tilbake-knappen) eller skjult: vises med en gang.
      if (r.bottom < 0 || r.height === 0) { ferdig(el); continue; }
      if (radTopp !== null && Math.abs(r.top - radTopp) < 4) nr++; else { nr = 0; radTopp = r.top; }
      vis(el, Math.min(nr, 5));
    }
    if (!venter.size) stopp();
  }

  const planlegg = () => { if (!planlagt) { planlagt = true; requestAnimationFrame(sjekk); } };
  let ro = null;
  function start() {
    if (lytter) return;
    lytter = true;
    window.addEventListener('scroll', planlegg, { passive: true });
    window.addEventListener('resize', planlegg);
    window.addEventListener('load', planlegg);
    if (window.ResizeObserver) { ro = new ResizeObserver(planlegg); ro.observe(main); }
  }
  function stopp() {
    if (!lytter) return;
    lytter = false;
    window.removeEventListener('scroll', planlegg);
    window.removeEventListener('resize', planlegg);
    window.removeEventListener('load', planlegg);
    if (ro) { ro.disconnect(); ro = null; }
  }

  function registrer(enheter) {
    enheter.forEach((el) => { el.setAttribute('data-ek-vis', ''); venter.add(el); });
    if (enheter.length) start();
  }

  // Sent lastede bilder tones inn (www: .lazyload -> .lazyloaded, 0,5 s).
  function toneBilder(rot) {
    rot.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      if (img.complete || img.closest(UNNTAK)) return;
      img.classList.add('ek-bilde-venter');
      const klar = () => {
        img.classList.add('ek-bilde-inn');
        img.classList.remove('ek-bilde-venter');
        setTimeout(() => img.classList.remove('ek-bilde-inn'), 600);
      };
      img.addEventListener('load', klar, { once: true });
      img.addEventListener('error', klar, { once: true });
      if (img.complete) klar();
    });
  }

  try {
    html.classList.add('ek-bev');
    const enheter = [];
    for (const c of main.children) finnEnheter(c, enheter);
    registrer(enheter);
    toneBilder(main);

    // Kopier av en skjult blokk (andre skript som kloner eller flytter noder) skal aldri bli stående usynlige.
    new MutationObserver((liste) => {
      for (const m of liste) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.hasAttribute('data-ek-vis') && !venter.has(n)) ferdig(n);
          n.querySelectorAll('[data-ek-vis]').forEach((x) => { if (!venter.has(x)) ferdig(x); });
        });
      }
    }).observe(main, { childList: true, subtree: true });

    // To bilderuter, så den skjulte tilstanden er tegnet før det som allerede er i bildet tones inn.
    requestAnimationFrame(() => requestAnimationFrame(sjekk));
  } catch (feil) {
    html.classList.remove('ek-bev');
    venter.forEach(ferdig);
    venter.clear();
    stopp();
    if (window.console) console.warn('ek-bevegelse:', feil);
  }
})();
